import { cloneDeep } from 'lodash';
import { Vector2 } from 'three';

type Box = {
  axisDirection: Vector2[]; // 代表矩形方向的两个轴向量
  supportVertexIdx: number[]; // 和矩形的 4 条边相交的支持点的索引，次序为 [bottom, right, top, left]
  sqrLenU0: number; // axisDirection[0] 长度的平方，用于避免浮点运算中的归一化来提高精度
  area: number; // 矩形的面积
};

const _diff = new Vector2();
const _v = new Vector2();
const _perpEdge = new Vector2(0, 0);

/**
 * 采用旋转卡壳法，由凸包点集生成最小面积矩形，时间复杂度为O(n)，算法详情见：https://www.geometrictools.com/Documentation/MinimumAreaRectangle.pdf
 * @param hull 输入为逆时针排序的凸多边形的顶点，输入点不能包含三个连续共线的点。
 * @returns 最小面积矩形的旋转角度、宽、高及中心点
 */
export const getMinBoxByRotatingCaliper = (hull: [number, number][]) => {
  const vertices = hull.map((point) => new Vector2(point[0], point[1]));
  const visited = new Array(vertices.length).fill(false);

  // 计算初始矩形
  let minBox = {
    axisDirection: [new Vector2(), new Vector2()],
    supportVertexIdx: [0, 0, 0, 0],
    sqrLenU0: 0,
    area: 0,
  };
  computeSmallestBox(vertices.length - 1, 0, vertices, minBox);
  visited[minBox.supportVertexIdx[0]] = true;

  const box = cloneDeep(minBox);

  // 通过最小的角度旋转来逐步更新矩形
  for (let i = 0; i < vertices.length; ++i) {
    // 找到与当前矩形边形成最小角度的多边形边
    const minAngleIdx = getMinAngleIdx(vertices, box);
    if (!minAngleIdx) {
      break;
    }
    // 更新支持点 & 计算新矩形的面积
    if (!updateSupport(minAngleIdx, vertices, visited, box)) {
      break;
    }
    // 判断最小面积矩形
    if (box.area <= minBox.area) {
      minBox = cloneDeep(box);
    }
  }

  return getCenterSizeAngle(minBox, vertices);
};

function getMinAngleIdx(vertices: Vector2[], box: Box): number[] | null {
  let angleNum = 0;
  let minAngle = Number.MAX_SAFE_INTEGER;
  let minAngleIdx: number[] = [];

  // 计算 每个支持点相连的边 和 当前矩形 的夹角
  for (let currIdxOfBox = 0; currIdxOfBox < 4; currIdxOfBox++) {
    const nextIdxOfBox = (currIdxOfBox + 1) % 4;

    // 只计算不重复的支持点
    if (box.supportVertexIdx[currIdxOfBox] !== box.supportVertexIdx[nextIdxOfBox]) {
      // [bottom, right, top, left] 取 [u0, u1, -u0, -u1]
      const direction = currIdxOfBox % 2 === 0 ? box.axisDirection[0] : box.axisDirection[1];
      if (currIdxOfBox >= 2) {
        direction.negate();
      }

      // 确定和当前支持点相连的边 edge
      const j0 = box.supportVertexIdx[currIdxOfBox];
      let j1 = j0 + 1;
      if (j1 >= vertices.length) {
        j1 = 0;
      }
      const edge = vertices[j1].clone().sub(vertices[j0]);

      // 计算夹角的正弦值的平方（根据正弦值的平方来找最小角度）
      _perpEdge.set(-edge.y, edge.x);
      const angleSinSqr = _perpEdge.dot(direction) ** 2 / edge.lengthSq();

      // 记录所有最小角度
      if (!minAngleIdx.length || angleSinSqr < minAngle) {
        minAngle = angleSinSqr;
        minAngleIdx = [currIdxOfBox];
      } else if (angleSinSqr === minAngle) {
        minAngleIdx.push(currIdxOfBox);
      }

      angleNum++;
    }
  }

  return angleNum > 0 ? minAngleIdx : null;
}

function updateSupport(minAngleIdx: number[], vertices: Vector2[], visited: boolean[], box: Box): boolean {
  // 考虑支持点重合的情况，对于所有角度最小的，都 + 1
  for (const idxOfBox of minAngleIdx) {
    box.supportVertexIdx[idxOfBox] = box.supportVertexIdx[idxOfBox] + 1;
    if (box.supportVertexIdx[idxOfBox] >= vertices.length) {
      box.supportVertexIdx[idxOfBox] = 0;
    }
  }

  // 以夹角最小的支持点的下一个点作为新的“底部”点
  const j = minAngleIdx[0]; // j 属于 {0, 1, 2, 3} 为 indexOfBox
  const bottom = box.supportVertexIdx[j];
  if (visited[bottom]) {
    // 已经处理过这个多边形边。
    return false;
  }
  visited[bottom] = true;

  // 确定新的轴向
  let j0 = bottom - 1;
  if (j0 < 0) {
    j0 = vertices.length - 1;
  }

  box.axisDirection[0].copy(vertices[bottom]).sub(vertices[j0]);
  box.axisDirection[1].set(-box.axisDirection[0].y, box.axisDirection[0].x);
  box.sqrLenU0 = box.axisDirection[0].lengthSq();

  // 根据现有的支持点去更新支持点，I' = {i_j + 1, i_{j+1}, i_{j+2}, i_{j+3}}
  const nextIndex = [bottom];
  for (let k = 1; k < 4; ++k) {
    nextIndex.push(box.supportVertexIdx[(j + k) % 4]);
  }
  box.supportVertexIdx = nextIndex;

  // 计算面积
  const [right, top, left] = box.supportVertexIdx.slice(1);
  const width = vertices[right].clone().sub(vertices[left]).dot(box.axisDirection[0]);
  const height = vertices[top].clone().sub(vertices[bottom]).dot(box.axisDirection[1]);
  box.area = (width * height) / box.sqrLenU0;
  return true;
}

/**
 * 根据 vertices[i0] 和 vertices[i1] 确定和这条边共线的矩形
 * @param i0
 * @param i1 作为原点的顶点的 index
 * @param vertices 所有顶点
 * @param box 用于把结果更新到这个 Box 对象中
 * @returns
 */
function computeSmallestBox(i0: number, i1: number, vertices: Vector2[], box: Box) {
  // 计算矩形两条边的方向（axisDirection）
  const origin = vertices[i1];
  box.axisDirection[0].subVectors(origin, vertices[i0]);
  box.axisDirection[1].set(-box.axisDirection[0].y, box.axisDirection[0].x);
  box.sqrLenU0 = box.axisDirection[0].lengthSq();

  // 和矩形的 4 条边相交的 vertex 在矩形坐标系的坐标，次序为 [bottom, right, top, left]，都初始化为 0
  const support = [new Vector2(0, 0), new Vector2(0, 0), new Vector2(0, 0), new Vector2(0, 0)];

  vertices.forEach((vertex, i) => {
    // 计算顶点在矩形坐标系的坐标 _v
    _diff.subVectors(vertex, origin);
    _v.set(box.axisDirection[0].dot(_diff), box.axisDirection[1].dot(_diff));

    // _v.x 最大的顶点作为矩形的 right 边
    if (_v.x > support[1].x || (_v.x === support[1].x && _v.y > support[1].y)) {
      box.supportVertexIdx[1] = i;
      support[1].copy(_v);
    }

    // _v.y 最大的顶点作为矩形的 top 边
    if (_v.y > support[2].y || (_v.y === support[2].y && _v.x < support[2].x)) {
      box.supportVertexIdx[2] = i;
      support[2].copy(_v);
    }

    // _v.x 最小的顶点作为矩形的 left 边
    if (_v.x < support[3].x || (_v.x === support[3].x && _v.y < support[3].y)) {
      box.supportVertexIdx[3] = i;
      support[3].copy(_v);
    }
  });

  // 计算面积
  const scaledWidth = support[1].x - support[3].x; // right - left
  const scaledHeight = support[2].y; // top - bottom (为0)
  box.area = (scaledWidth * scaledHeight) / box.sqrLenU0; // 间接归一化
}

/**
 * 计算矩形的旋转角度、宽、高及中心点
 * @param box
 * @param vertices
 * @returns 矩形的旋转角度、宽、高及中心点
 */
const getCenterSizeAngle = (box: Box, vertices: Vector2[]) => {
  const angle = Math.atan2(box.axisDirection[0].y, box.axisDirection[0].x);

  const u0 = box.axisDirection[0].clone().normalize();
  const u1 = box.axisDirection[1].clone().normalize();

  const [bottom, right, top, left] = box.supportVertexIdx;
  const width = Math.abs(vertices[right].clone().sub(vertices[left]).dot(u0));
  const height = Math.abs(vertices[top].clone().sub(vertices[bottom]).dot(u1));

  const origin = vertices[bottom - 1 === -1 ? vertices.length - 1 : bottom - 1].clone();
  const maxV = u0.clone().multiplyScalar(vertices[left].clone().sub(origin).dot(u0));
  const vw = u0.clone().multiplyScalar(width * 0.5);
  const vh = u1.clone().multiplyScalar(height * 0.5);
  const center = maxV.add(origin).add(vw).add(vh).toArray();
  return { angle, width, height, center };
};
